import { HttpErrorResponse } from '@angular/common/http';
import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../../../services/auth.service';

/** Mirrors app.module's forms.validation.password block. */
const MIN_LENGTH = 8;
const MAX_LENGTH = 50;

interface Rule {
  key: string;
  met: (password: string) => boolean;
}

@Component({
  selector: 'app-password-reset',
  templateUrl: './password-reset.component.html',
  styleUrl: './password-reset.component.scss',
})
export class PasswordResetComponent {
  password = '';
  confirmPassword = '';
  showPassword = false;

  loading = false;
  success = false;
  error = '';

  /** The link is unusable before anything is typed — ask for a new one. */
  linkExpired = false;
  linkMissing = false;

  resendEmail = '';
  resendLoading = false;
  resendSuccess = false;
  resendError = '';

  private readonly token: string;
  private accountEmail = '';

  /**
   * The rules the server enforces, restated so they can be read before the
   * password is submitted rather than guessed from a rejection afterwards.
   */
  readonly rules: Rule[] = [
    { key: 'length', met: (p) => p.length >= MIN_LENGTH && p.length <= MAX_LENGTH },
    { key: 'notOnlyDigits', met: (p) => !/^\d+$/.test(p) },
    { key: 'varied', met: (p) => new Set(this.fold(p)).size >= 5 },
    { key: 'notCommon', met: (p) => !this.isObvious(p) },
    { key: 'noEmail', met: (p) => !this.containsIdentity(p, this.accountEmail) },
  ];

  constructor(private router: Router, private authService: AuthService) {
    const params = this.router.parseUrl(this.router.url).queryParams;
    this.token = params['reset_password_token'] ?? '';

    if (!this.token) {
      this.linkMissing = true;
      return;
    }

    // The token is a JWT carrying the account's email and its own expiry.
    // Reading it here means an expired link says so immediately, instead of
    // after the user has thought up a password and typed it twice.
    const claims = this.readClaims(this.token);
    this.accountEmail = claims?.email ?? '';
    this.resendEmail = this.accountEmail;
    if (claims?.exp && claims.exp * 1000 < Date.now()) {
      this.linkExpired = true;
    }
  }

  get passwordsMatch(): boolean {
    return this.confirmPassword.length === 0 || this.password === this.confirmPassword;
  }

  get canSubmit(): boolean {
    return (
      !this.loading &&
      this.password.length > 0 &&
      this.password === this.confirmPassword &&
      this.rules.every((rule) => rule.met(this.password))
    );
  }

  ruleMet(rule: Rule): boolean {
    return this.password.length > 0 && rule.met(this.password);
  }

  submit(): void {
    if (!this.canSubmit) {
      return;
    }
    this.loading = true;
    this.error = '';

    this.authService
      .confirmPasswordReset(this.token, this.password, this.confirmPassword)
      .subscribe({
        next: () => {
          this.loading = false;
          this.success = true;
          setTimeout(() => this.router.navigate(['/auth/login']), 2500);
        },
        error: (err: HttpErrorResponse) => {
          this.loading = false;
          const code = err.headers?.get('X-Error-Code') ?? '';
          if (code === 'reset_token_expired' || code === 'reset_token_invalid') {
            this.linkExpired = true;
            return;
          }
          this.error = this.messageFor(code, err);
        },
      });
  }

  requestNewLink(): void {
    if (!this.resendEmail || this.resendLoading) {
      return;
    }
    this.resendLoading = true;
    this.resendError = '';

    this.authService.requestPasswordReset(this.resendEmail).subscribe({
      next: () => {
        this.resendLoading = false;
        this.resendSuccess = true;
      },
      error: () => {
        this.resendLoading = false;
        // Whether the address exists is not something this page should
        // disclose, so the failure reads the same either way.
        this.resendError = 'pages.passwordReset.resendError';
      },
    });
  }

  toLogin(): void {
    this.router.navigate(['/auth/login']);
  }

  /** Translation key for a rejection, falling back to the server's sentence. */
  private messageFor(code: string, err: HttpErrorResponse): string {
    const known = [
      'password_mismatch',
      'password_missing',
      'password_too_short',
      'password_too_long',
      'password_digits_only',
      'password_not_varied',
      'password_common',
      'password_contains_email',
      'password_contains_name',
    ];
    if (known.includes(code)) {
      return `pages.passwordReset.errors.${code}`;
    }
    return err.error?.detail || 'pages.passwordReset.errors.generic';
  }

  private readClaims(token: string): { email?: string; exp?: number } | null {
    try {
      const payload = token.split('.')[1];
      if (!payload) {
        return null;
      }
      const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(json);
    } catch {
      return null;
    }
  }

  private fold(value: string): string {
    return (value || '')
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase();
  }

  private isObvious(password: string): boolean {
    const obvious = [
      'password', 'passwort', '123456', '12345678', '123456789', '1234567890',
      'qwertz', 'qwerty', 'qwertzuiop', 'letmein', 'welcome', 'willkommen',
      'admin', 'administrator', 'iloveyou', 'monkey', 'dragon', 'sunshine',
      'alphabees', 'alphalearn', 'alphatutor',
    ];
    const folded = this.fold(password);
    const stem = folded.replace(/[^a-z]+$/, '');
    return obvious.includes(folded) || (!!stem && obvious.includes(stem));
  }

  /** True when the password carries a recognisable part of email or name. */
  private containsIdentity(password: string, identity: string): boolean {
    if (!identity) {
      return false;
    }
    const folded = this.fold(password);
    const local = this.fold(identity.split('@')[0]);
    if (local.length >= 3 && folded.includes(local)) {
      return true;
    }
    return local
      .split(/[^a-z0-9]+/)
      .some((part) => part.length >= 4 && folded.includes(part));
  }
}
